import React, { useEffect, useRef, useState } from 'react';
import { Share2, Mail, Facebook, Linkedin, Twitter, Link as LinkIcon, Check } from 'lucide-react';
import { toast } from 'sonner';

// Named the "X" icon Twitter for lucide compatibility but label it as X in the UI.

export const ShareMenu = ({
  title,
  text,
  url,
  emailSubject,
  emailBody,
  testIdPrefix = 'share',
  buttonClassName = '',
  buttonLabel = 'Share',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encUrl = encodeURIComponent(shareUrl);
  const encText = encodeURIComponent(text || title || '');
  const encTitle = encodeURIComponent(title || '');

  const openWin = (u) => window.open(u, '_blank', 'noopener,noreferrer,width=680,height=560');

  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        setOpen(false);
        return;
      } catch { /* user cancelled */ }
    }
    // fallback: open menu
    setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const targets = [
    {
      key: 'email',
      label: 'Email',
      icon: Mail,
      onClick: () => {
        const subject = encodeURIComponent(emailSubject || title || 'Check this out');
        const body = encodeURIComponent(`${emailBody || text || title || ''}\n\n${shareUrl}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      },
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      onClick: () => openWin(`https://www.facebook.com/sharer/sharer.php?u=${encUrl}`),
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      onClick: () => openWin(`https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`),
    },
    {
      key: 'x',
      label: 'X',
      icon: Twitter,
      onClick: () => openWin(`https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`),
    },
    {
      key: 'copy',
      label: copied ? 'Copied!' : 'Copy Link',
      icon: copied ? Check : LinkIcon,
      onClick: copyLink,
    },
  ];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={handleNative}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-toggle`}
        className={
          buttonClassName ||
          `inline-flex items-center gap-1.5 text-[${compact ? '11.5' : '12.5'}px] font-semibold text-aawta-navy hover:text-aawta-magenta rounded-full px-3 py-1.5 border border-aawta-border bg-white hover:border-aawta-magenta/40 transition-colors`
        }
      >
        <Share2 className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        {buttonLabel && <span>{buttonLabel}</span>}
      </button>

      {open && (
        <div
          role="menu"
          data-testid={`${testIdPrefix}-menu`}
          className="absolute right-0 z-40 mt-2 w-52 rounded-xl border border-aawta-border bg-white shadow-aawta-elevated overflow-hidden"
        >
          {targets.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { t.onClick(); if (t.key !== 'copy') setOpen(false); }}
                data-testid={`${testIdPrefix}-${t.key}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-aawta-navy hover:bg-aawta-surface text-left"
              >
                <Icon className="h-4 w-4 text-aawta-magenta shrink-0" />
                <span className="font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
